import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listarTrabalhos, criarTrabalho, deletarTrabalho, uploadImagem } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const AdminGaleria = () => {
  const { token } = useAuth();
  const [trabalhos, setTrabalhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    categoria: 'casamento',
    imagem: ''
  });

  const categorias = [
    { id: 'casamento', nome: 'Casamento' },
    { id: 'aniversario', nome: 'Aniversário' },
    { id: 'evento', nome: 'Evento' },
    { id: 'outro', nome: 'Outro' }
  ];

  useEffect(() => {
    carregarTrabalhos();
  }, []);

  const carregarTrabalhos = async () => {
    try {
      const data = await listarTrabalhos();
      setTrabalhos(data);
    } catch (error) {
      toast.error('Erro ao carregar trabalhos');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const response = await uploadImagem(file, token);
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.url}`;
      setFormData({ ...formData, imagem: imageUrl });
      toast.success('Imagem enviada com sucesso!');
    } catch (error) {
      toast.error('Erro ao enviar imagem');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await criarTrabalho(formData, token);
      toast.success('Trabalho adicionado!');
      setDialogAberto(false);
      resetForm();
      carregarTrabalhos();
    } catch (error) {
      toast.error('Erro ao adicionar trabalho');
    }
  };

  const handleDeletar = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este trabalho?')) return;

    try {
      await deletarTrabalho(id, token);
      toast.success('Trabalho deletado!');
      carregarTrabalhos();
    } catch (error) {
      toast.error('Erro ao deletar trabalho');
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      categoria: 'casamento',
      imagem: ''
    });
  };

  const handleNovo = () => {
    resetForm();
    setDialogAberto(true);
  };

  return (
    <div className="admin-galeria">
      <div className="admin-page-header">
        <h2 className="admin-page-title">Gestão de Galeria</h2>
        <Button onClick={handleNovo} className="btn-primary">
          <Plus size={20} /> Novo Trabalho
        </Button>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : trabalhos.length === 0 ? (
        <p className="admin-empty">Nenhum trabalho na galeria ainda. Adicione o primeiro!</p>
      ) : (
        <div className="admin-galeria-grid">
          {trabalhos.map((trabalho) => (
            <Card key={trabalho.id} className="admin-galeria-card">
              <div className="admin-galeria-imagem">
                <img src={trabalho.imagem} alt={trabalho.titulo} />
              </div>
              <CardContent className="admin-galeria-content">
                <h3>{trabalho.titulo}</h3>
                <p className="galeria-categoria">{categorias.find(c => c.id === trabalho.categoria)?.nome}</p>
                <p className="galeria-descricao">{trabalho.descricao}</p>
                <Button onClick={() => handleDeletar(trabalho.id)} variant="destructive" size="sm" className="delete-btn">
                  <Trash2 size={16} /> Deletar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog para adicionar */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="admin-dialog">
          <DialogHeader>
            <DialogTitle>Novo Trabalho Realizado</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="admin-form">
            <div className="form-group">
              <label>Título *</label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Descrição *</label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows={3}
                required
              />
            </div>

            <div className="form-group">
              <label>Categoria *</label>
              <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <label>Imagem *</label>
              <div className="image-upload-container">
                {formData.imagem && (
                  <img src={formData.imagem} alt="Preview" className="image-preview" />
                )}
                <label htmlFor="image-upload" className="image-upload-btn">
                  <Upload size={20} />
                  {uploadingImage ? 'Enviando...' : 'Escolher Imagem'}
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                    required={!formData.imagem}
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogAberto(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!formData.imagem}>
                Adicionar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGaleria;
